import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform, AppState, type AppStateStatus } from 'react-native';
import * as Notifications from 'expo-notifications';

import type { SummaryPreferences, SummaryVariant } from '@/types/home-summary';
import { NotificationPreferencesManager } from './NotificationPreferencesManager';

const IS_NATIVE = Platform.OS !== 'web';

const STORAGE_PREFS = 'fimfam-ai:summary:prefs';
const STORAGE_LAST_RUN = 'fimfam-ai:summary:last-run';

type LastRunMap = Partial<Record<SummaryVariant, string>>;

type MissedNotification = {
  variant: SummaryVariant;
  scheduledTime: Date;
  targetDate: string;
  title: string;
  body: string;
};

class MissedNotificationManager {
  private isInitialized = false;
  private appStateSubscription: { remove: () => void } | null = null;

  async initialize(): Promise<void> {
    if (!IS_NATIVE) {
      this.isInitialized = true;
      return;
    }
    if (this.isInitialized) {
      return;
    }

    try {
      this.appStateSubscription = AppState.addEventListener('change', this.handleAppStateChange);
      this.isInitialized = true;
    } catch (error) {
      console.error('[missedNotificationManager] initialize failed', error);
    }
  }

  cleanup(): void {
    if (this.appStateSubscription) {
      this.appStateSubscription.remove();
      this.appStateSubscription = null;
    }
    this.isInitialized = false;
  }

  private handleAppStateChange = async (state: AppStateStatus): Promise<void> => {
    if (state === 'active') {
      await this.checkAndDeliverMissedNotifications();
    }
  };

  private async checkAndDeliverMissedNotifications(): Promise<void> {
    try {
      const preferencesRaw = await AsyncStorage.getItem(STORAGE_PREFS);
      if (!preferencesRaw) {
        return;
      }

      const preferences: SummaryPreferences = JSON.parse(preferencesRaw);
      const timezone = preferences.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
      const now = new Date();

      const missedNotifications = this.findMissedNotifications(preferences, timezone, now);

      if (missedNotifications.length === 0) {
        return;
      }

      const notificationPrefsManager = NotificationPreferencesManager.getInstance();
      await notificationPrefsManager.initialize();

      const lastRunRaw = await AsyncStorage.getItem(STORAGE_LAST_RUN);
      const lastRun: LastRunMap = lastRunRaw ? JSON.parse(lastRunRaw) : {};
      const nextLastRun = { ...lastRun };

      for (const notification of missedNotifications) {
        const alreadyDelivered = lastRun[notification.variant] === notification.targetDate;
        if (alreadyDelivered) {
          continue;
        }

        if (!this.shouldDeliver(notification.variant, notificationPrefsManager)) {
          continue;
        }

        await this.deliverImmediateNotification(notification);
        nextLastRun[notification.variant] = notification.targetDate;
      }

      await AsyncStorage.setItem(STORAGE_LAST_RUN, JSON.stringify(nextLastRun));
    } catch (error) {
      console.error('[missedNotificationManager] checkAndDeliverMissedNotifications failed', error);
    }
  }

  private shouldDeliver(
    variant: SummaryVariant,
    preferencesManager: NotificationPreferencesManager,
  ): boolean {
    const preferenceKey = variant === 'morning-brief' ? 'morningBrief' : 'eveningRecap';
    return preferencesManager.shouldDeliverNotification(preferenceKey);
  }

  private findMissedNotifications(
    preferences: SummaryPreferences,
    timezone: string,
    now: Date,
  ): MissedNotification[] {
    const missed: MissedNotification[] = [];
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const configs = [
      {
        variant: 'morning-brief' as SummaryVariant,
        enabled: preferences.morningBrief.enabled,
        time: preferences.morningBrief.time,
        title: 'Morning brief ready',
        body: "Open FimFam to review today's plan.",
      },
      {
        variant: 'evening-recap' as SummaryVariant,
        enabled: preferences.eveningRecap.enabled,
        time: preferences.eveningRecap.time,
        title: 'Evening recap ready',
        body: "See what was finished and what's coming tomorrow.",
      },
    ];

    for (const config of configs) {
      if (!config.enabled) {
        continue;
      }

      // Check today's scheduled time
      const todayScheduled = this.buildScheduledTime(config.time, timezone, now, 0);
      if (todayScheduled <= now && todayScheduled >= twentyFourHoursAgo) {
        const targetDate = this.formatDate(now, timezone);
        missed.push({
          variant: config.variant,
          scheduledTime: todayScheduled,
          targetDate,
          title: config.title,
          body: config.body,
        });
      }

      // Check yesterday's scheduled time (in case it was within 24h)
      const yesterdayScheduled = this.buildScheduledTime(config.time, timezone, now, -1);
      if (yesterdayScheduled <= now && yesterdayScheduled >= twentyFourHoursAgo) {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const targetDate = this.formatDate(yesterday, timezone);
        missed.push({
          variant: config.variant,
          scheduledTime: yesterdayScheduled,
          targetDate,
          title: config.title,
          body: config.body,
        });
      }
    }

    return missed;
  }

  private buildScheduledTime(time: string, timezone: string, reference: Date, dayOffset: number): Date {
    const target = new Date(reference);
    target.setDate(target.getDate() + dayOffset);

    const [hours, minutes] = time.split(':').map(segment => parseInt(segment, 10));
    target.setHours(Number.isFinite(hours) ? hours : 9, Number.isFinite(minutes) ? minutes : 0, 0, 0);

    // Convert to correct timezone
    const localeString = target.toLocaleString('en-US', { timeZone: timezone });
    return new Date(localeString);
  }

  private formatDate(date: Date, timezone: string): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  private async deliverImmediateNotification(notification: MissedNotification): Promise<void> {
    try {
      await Notifications.scheduleNotificationAsync({
        content: {
          title: notification.title,
          body: notification.body,
          sound: 'default',
          data: { variant: notification.variant },
        },
        trigger: null, // Immediate delivery
      });
    } catch (error) {
      console.error(
        `[missedNotificationManager] failed to deliver notification for ${notification.variant}`,
        error,
      );
    }
  }
}

export const missedNotificationManager = new MissedNotificationManager();
